import { autoUpdate, flip, shift, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import clsx from "clsx";
import { motion, Variants } from "framer-motion";
import { useState } from "react";
import { Icon, IconName } from "..";

type Props = Readonly<{
  children: React.ReactNode;
  trigger?: React.ReactNode;
  icon?: IconName;
  className?: string;
  iconClassName?: string;
  rotateToggler?: boolean;
  closeOnContentClick?: boolean;
  "aria-label"?: string;
}>;

export const Dropdown = ({
  children,
  trigger,
  icon = "KeyboardArrowDown",
  className,
  iconClassName,
  rotateToggler = true,
  closeOnContentClick = false,
  "aria-label": ariaLabel,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    strategy: "fixed",
    middleware: [flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const variants: Variants = {
    hidden: { opacity: 0, transitionEnd: { display: "none" } },
    visible: { opacity: 1, display: "block" },
  };

  return (
    <div className={clsx("relative inline-block align-middle", className)} aria-label={ariaLabel}>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="flex overflow-hidden"
        onClick={trigger ? () => setIsOpen(!isOpen) : undefined}
      >
        {trigger ?? (
          <Icon
            name={icon}
            onClick={() => setIsOpen(!isOpen)}
            className={clsx(iconClassName, {
              "rotate-180": isOpen && rotateToggler,
            })}
          />
        )}
      </div>

      <div
        className="z-20 mt-2"
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps()}
        onClick={closeOnContentClick ? () => setIsOpen(false) : undefined}
      >
        <motion.div
          variants={variants}
          initial="hidden"
          animate={isOpen ? "visible" : "hidden"}
          exit="hidden"
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
};
